import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Send, ArrowLeft, Flag, Ban, Handshake, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const { user } = useAuth();
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const toUserId = searchParams.get('to');

  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(conversationId || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMobile, setShowMobile] = useState(!conversationId);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get('/conversations');
      setConversations(data.conversations || []);
    } catch (err) {
      console.debug('fetchConversations failed:', err?.message);
    }
  }, []);

  const fetchMessages = useCallback(async (convoId) => {
    if (!convoId) return;
    try {
      const { data } = await api.get(`/conversations/${convoId}/messages`);
      setMessages(data.messages || []);
    } catch (err) {
      console.debug('fetchMessages failed:', err?.message);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (activeConvo) {
      fetchMessages(activeConvo);
      setShowMobile(false);
    }
  }, [activeConvo, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket
  const activeConvoRef = useRef(activeConvo);
  activeConvoRef.current = activeConvo;

  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.id}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_message') {
          if (msg.conversation_id === activeConvoRef.current) {
            setMessages(prev => [...prev, msg]);
          }
          fetchConversations();
        }
      } catch (err) {
        console.debug('WebSocket message parse failed:', err?.message);
      }
    };

    ws.onclose = () => {
      setTimeout(() => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
      }, 3000);
    };

    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fetchConversations]);

  // Handle "to" param - initiate new conversation
  useEffect(() => {
    if (toUserId && user) {
      // Check if conversation already exists
      const existing = conversations.find(c => c.participants?.includes(toUserId));
      if (existing) {
        setActiveConvo(existing.conversation_id);
      }
    }
  }, [toUserId, conversations, user]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setLoading(true);
    try {
      let recipientId;
      if (activeConvo) {
        const convo = conversations.find(c => c.conversation_id === activeConvo);
        recipientId = convo?.participants?.find(p => p !== user.id);
      } else if (toUserId) {
        recipientId = toUserId;
      }
      if (!recipientId) { toast.error('No recipient selected'); setLoading(false); return; }

      const { data } = await api.post('/messages/send', { recipient_id: recipientId, content: newMessage });
      setMessages(prev => [...prev, data.message]);
      if (!activeConvo && data.conversation_id) setActiveConvo(data.conversation_id);
      setNewMessage('');
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setLoading(false); }
  };

  const handleReport = async (userId) => {
    const reason = window.prompt('Please describe the issue:');
    if (!reason) return;
    try {
      await api.post('/reports', { reported_user_id: userId, reason });
      toast.success('Report submitted');
    } catch { toast.error('Failed to submit report'); }
  };

  const handleBlock = async (userId) => {
    if (!window.confirm('Block this player? They won\'t be able to message you.')) return;
    try {
      await api.post('/players/block', { user_id: userId });
      toast.success('Player blocked');
    } catch { toast.error('Failed to block player'); }
  };

  const handleConfirmMeetup = async () => {
    if (!activeConvo) return;
    try {
      const { data } = await api.post(`/conversations/${activeConvo}/confirm-meetup`);
      setConversations(prev => prev.map(c =>
        c.conversation_id === activeConvo
          ? { ...c, meetup_confirmed_by: data.meetup_confirmed_by || [user?.id] }
          : c
      ));
      toast.success("Meetup confirmed! You can now leave a review.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm meetup');
    }
  };

  const submitReview = async () => {
    if (!otherUser?.id) return;
    setReviewSubmitting(true);
    try {
      await api.post('/reviews', {
        reviewed_user_id: otherUser.id,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      toast.success('Thanks for your review!');
      setReviewOpen(false);
      setReviewRating(5);
      setReviewComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const activeConvoData = conversations.find(c => c.conversation_id === activeConvo);
  const otherUser = activeConvoData?.other_user;
  const meetupConfirmedByMe = (activeConvoData?.meetup_confirmed_by || []).includes(user?.id);

  return (
    <div data-testid="messages-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
        <div className="flex h-full">
          {/* Conversation List */}
          <div className={`w-full md:w-80 border-r border-slate-100 flex flex-col ${!showMobile && activeConvo ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h2>
            </div>
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">No conversations yet</div>
              ) : conversations.map(convo => (
                <button
                  key={convo.conversation_id}
                  onClick={() => setActiveConvo(convo.conversation_id)}
                  className={`w-full text-left p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeConvo === convo.conversation_id ? 'bg-[#56dfb4]/5' : ''}`}
                  data-testid={`convo-${convo.conversation_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#56c6df]/15 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {(convo.other_user?.display_name || 'U')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{convo.other_user?.display_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500 truncate">{convo.last_message || 'Start chatting'}</p>
                    </div>
                    {(convo.unread_count?.[user?.id] || 0) > 0 && (
                      <span className="bg-[#df5681] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {convo.unread_count[user?.id]}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${showMobile && !activeConvo ? 'hidden md:flex' : 'flex'}`}>
            {activeConvo || toUserId ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                  <button onClick={() => { setShowMobile(true); setActiveConvo(null); }} className="md:hidden p-1">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-8 h-8 rounded-full bg-[#56c6df]/15 flex items-center justify-center text-sm font-bold">
                    {(otherUser?.display_name || 'U')[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{otherUser?.display_name || 'New Conversation'}</p>
                    <p className="text-xs text-slate-500 capitalize">{otherUser?.skill_level || ''}</p>
                  </div>
                  {otherUser && (
                    <div className="flex gap-1">
                      {!meetupConfirmedByMe ? (
                        <Button variant="ghost" size="sm" onClick={handleConfirmMeetup} className="text-slate-400 hover:text-[#56dfb4]" data-testid="confirm-meetup-btn" title="Confirm meetup">
                          <Handshake className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setReviewOpen(true)} className="text-slate-400 hover:text-[#f59e0b]" data-testid="leave-review-btn" title="Leave a review">
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleReport(otherUser.id)} className="text-slate-400 hover:text-red-500" data-testid="report-user-btn" title="Report">
                        <Flag className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleBlock(otherUser.id)} className="text-slate-400 hover:text-red-500" data-testid="block-user-btn" title="Block">
                        <Ban className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((msg, i) => {
                      const isMine = msg.sender_id === user?.id;
                      return (
                        <div key={msg.message_id || i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                            isMine ? 'bg-[#56dfb4] text-slate-900' : 'bg-[#f1f5f9] text-slate-700'
                          }`} data-testid={`message-${msg.message_id || i}`}>
                            <p>{msg.content}</p>
                            <p className={`text-[10px] mt-1 ${isMine ? 'text-slate-700/60' : 'text-slate-400'}`}>
                              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-4 border-t border-slate-100">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      placeholder="Type a message..."
                      className="rounded-full"
                      data-testid="message-input"
                    />
                    <Button onClick={handleSend} disabled={loading || !newMessage.trim()} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-4" data-testid="send-message-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm mt-1">Or find players to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leave Review Modal */}
      {reviewOpen && otherUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setReviewOpen(false)} data-testid="review-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review {otherUser.display_name}</h3>
            <p className="text-xs text-slate-500 mb-4">Your review is anonymous. Only paid members can see reviews, and admins can remove inappropriate ones.</p>

            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Your rating</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReviewRating(i)}
                    className="p-1"
                    data-testid={`star-${i}`}
                    aria-label={`${i} star${i === 1 ? '' : 's'}`}
                  >
                    <Star className={`w-7 h-7 transition-colors ${i <= reviewRating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-slate-300 hover:text-[#f59e0b]'}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Share your experience (optional)</p>
              <textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="e.g. Friendly, on time, fun to play with!"
                className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#56dfb4]"
                data-testid="review-comment"
              />
              <p className="text-xs text-slate-400 mt-1 text-right">{reviewComment.length}/500</p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setReviewOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={submitReview}
                disabled={reviewSubmitting}
                className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-6"
                data-testid="submit-review-btn"
              >
                {reviewSubmitting ? 'Submitting…' : 'Post Review'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
